/**
 * Design System - ButtonWithIcon Component
 * Button with icon and text label
 */

import React from 'react';
import {
  typographyColors,
  backgroundColors,
  spacing,
  borders,
  typography,
  fontPrimary,
} from '../../utils';

export interface ButtonWithIconProps {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  variant?: 'default' | 'outlined' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
}

export const ButtonWithIcon: React.FC<ButtonWithIconProps> = ({
  icon,
  label,
  onClick,
  variant = 'default',
  size = 'md',
  disabled = false,
}) => {
  const sizeConfig = {
    sm: {
      paddingX: spacing[2],
      paddingY: spacing[1.5],
      fontSize: typography.size.xs,
      gap: spacing[1.5],
    },
    md: {
      paddingX: spacing[3],
      paddingY: spacing[2],
      fontSize: typography.size.sm,
      gap: spacing[2],
    },
    lg: {
      paddingX: spacing[4],
      paddingY: spacing[3],
      fontSize: typography.size.base,
      gap: spacing[2],
    },
  };

  const config = sizeConfig[size];

  const getBackgroundColor = () => {
    if (disabled) return backgroundColors.shade;
    if (variant === 'ghost') return 'transparent';
    if (variant === 'outlined') return backgroundColors.secondary; // Light grey background for outlined variant
    return backgroundColors.primary;
  };

  const getBorderColor = () => {
    if (variant === 'ghost') return 'transparent';
    return backgroundColors.shade;
  };

  const getTextColor = () => {
    if (disabled) return typographyColors.tertiary;
    return typographyColors.primary;
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        backgroundColor: getBackgroundColor(),
        borderColor: getBorderColor(),
        borderWidth:
          variant === 'ghost' ? borders.width.none : borders.width.thin,
        borderStyle: 'solid',
        borderRadius: borders.radius.sm,
        paddingLeft: config.paddingX,
        paddingRight: config.paddingX,
        paddingTop: config.paddingY,
        paddingBottom: config.paddingY,
        display: 'flex',
        alignItems: 'center',
        gap: config.gap,
        cursor: onClick && !disabled ? 'pointer' : 'not-allowed',
        transition: 'all 0.2s',
        fontFamily: fontPrimary,
        fontSize: config.fontSize,
        fontWeight: typography.weight.regular,
        color: getTextColor(),
        opacity: disabled ? 0.5 : 1,
      }}
      onMouseEnter={e => {
        if (onClick && !disabled) {
          e.currentTarget.style.backgroundColor =
            variant === 'ghost' || variant === 'outlined'
              ? backgroundColors.secondary
              : backgroundColors.secondary;
        }
      }}
      onMouseLeave={e => {
        if (onClick && !disabled) {
          e.currentTarget.style.backgroundColor = getBackgroundColor();
        }
      }}
    >
      <span>{icon}</span>
      <span>{label}</span>
    </button>
  );
};
